import { HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { MsalService } from '@azure/msal-angular';
import { AccountInfo } from '@azure/msal-browser';
import { from, switchMap } from 'rxjs';
import { environment } from '../../../environments/environment';

const sidexApiUrls = Object.values(environment.apiUrls);

export const sidexApiAuthInterceptor: HttpInterceptorFn = (request, next) => {
    if (!isSidexApiRequest(request.url) || request.headers.has('Authorization')) {
        return next(request);
    }

    const msalService = inject(MsalService);
    const account = msalService.instance.getActiveAccount() ?? msalService.instance.getAllAccounts()[0] ?? null;

    if (!account) {
        console.error('No active MSAL account available for Sidex API request', request.url);
        return next(request);
    }

    return from(resolveBearerToken(msalService, account)).pipe(
        switchMap((token) => {
            if (!token) {
                console.error('No ID token or access token available for Sidex API request', request.url);
                return next(request);
            }

            return next(
                request.clone({
                    setHeaders: {
                        Authorization: `Bearer ${token}`
                    }
                })
            );
        })
    );
};

async function resolveBearerToken(msalService: MsalService, account: AccountInfo): Promise<string | null> {
    if (environment.apiTokenScopes.length > 0) {
        try {
            const authResult = await msalService.instance.acquireTokenSilent({
                account,
                scopes: environment.apiTokenScopes
            });

            return authResult.accessToken || authResult.idToken || account.idToken || null;
        } catch (error) {
            if (account.idToken) {
                console.warn('Failed to acquire a Sidex API access token, falling back to the ID token', error);
                return account.idToken;
            }

            console.error('Failed to acquire a token for Sidex API request', error);
            return null;
        }
    }

    return account.idToken ?? null;
}

function isSidexApiRequest(requestUrl: string): boolean {
    return sidexApiUrls.some((apiUrl) => matchesApiPrefix(requestUrl, apiUrl));
}

function matchesApiPrefix(requestUrl: string, apiUrl: string): boolean {
    return requestUrl === apiUrl || requestUrl.startsWith(`${apiUrl}/`) || requestUrl.startsWith(`${apiUrl}?`);
}
